import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Eye, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import MediaUpload from "@/components/admin/MediaUpload";
import RichTextEditor from "@/components/admin/RichTextEditor";

/**
 * A single reusable editor for all content types.
 * Different kinds show different field sets, but share the same shell.
 */

const FIELD_SETS = {
  articles: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "subtitle", label: "Subtitle / Deck", type: "text" },
    { key: "excerpt", label: "Excerpt (lede)", type: "textarea", rows: 3 },
    { key: "body_html", label: "Article body", type: "rich" },
    { key: "image", label: "Hero image", type: "media" },
    { key: "category", label: "Category", type: "text" },
    { key: "tags", label: "Tags (comma separated)", type: "tags" },
    { key: "author", label: "Author", type: "text" },
    { key: "author_role", label: "Author role", type: "text" },
    { key: "date", label: "Publish date", type: "date" },
    { key: "read_time", label: "Read time", type: "text", placeholder: "e.g. 8 min read" },
    { key: "slug", label: "Slug", type: "text", hint: "Auto-generated from title if left blank" },
    { key: "meta_title", label: "SEO title", type: "text", section: "SEO" },
    { key: "meta_description", label: "SEO description", type: "textarea", rows: 2, section: "SEO" },
  ],
  news: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "summary", label: "Summary", type: "textarea", rows: 4, required: true },
    { key: "image", label: "Image", type: "media" },
    { key: "category", label: "Category", type: "text" },
    { key: "author", label: "Author", type: "text" },
    { key: "date", label: "Publish date", type: "date" },
    { key: "read_time", label: "Read time", type: "text", placeholder: "e.g. 5 min read" },
    { key: "slug", label: "Slug", type: "text" },
    { key: "meta_title", label: "SEO title", type: "text", section: "SEO" },
    { key: "meta_description", label: "SEO description", type: "textarea", rows: 2, section: "SEO" },
  ],
  interviews: [
    { key: "name", label: "Subject name", type: "text", required: true },
    { key: "position", label: "Position", type: "text" },
    { key: "company", label: "Company", type: "text" },
    { key: "country", label: "Country", type: "text" },
    { key: "image", label: "Portrait", type: "media" },
    { key: "summary", label: "Summary", type: "textarea", rows: 4 },
    { key: "body_html", label: "Interview transcript", type: "rich" },
    { key: "date", label: "Publish date", type: "date" },
    { key: "read_time", label: "Read time", type: "text" },
    { key: "slug", label: "Slug", type: "text" },
  ],
  magazines: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "caption", label: "Cover caption", type: "text" },
    { key: "edition", label: "Edition (e.g. 'Issue No. 05')", type: "text" },
    { key: "issue_number", label: "Issue number", type: "text", placeholder: "e.g. 05" },
    { key: "year", label: "Year", type: "text", placeholder: "e.g. 2026" },
    { key: "release_month", label: "Release month", type: "text", placeholder: "e.g. July 2026" },
    { key: "category", label: "Category", type: "text" },
    { key: "cover_subject", label: "Cover subject / sub-headline", type: "textarea", rows: 2 },
    { key: "description", label: "Description", type: "textarea", rows: 4 },
    { key: "cover_image", label: "Cover image", type: "media" },
    { key: "pdf_url", label: "Magazine PDF", type: "media-pdf" },
    { key: "highlights", label: "Highlights (one per line)", type: "lines" },
    { key: "slug", label: "Slug", type: "text" },
  ],
};

const KIND_META = {
  articles: { label: "Article", publicPath: "/articles" },
  news: { label: "News item", publicPath: "/news" },
  interviews: { label: "Interview", publicPath: "/interviews" },
  magazines: { label: "Magazine", publicPath: "/magazine" },
};

const AdminEditorPage = () => {
  const { kind, id } = useParams();
  const navigate = useNavigate();
  const meta = KIND_META[kind];
  const fields = FIELD_SETS[kind] || [];
  const isEdit = Boolean(id);

  const [doc, setDoc] = useState({ status: "draft" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try { setDoc(await api.adminGet(kind, id)); } catch { toast.error("Failed to load"); }
      finally { setLoading(false); }
    })();
  }, [kind, id, isEdit]);

  const setField = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

  const save = async (publish = null) => {
    setSaving(true);
    try {
      const payload = { ...doc };
      // Coerce tags string → array
      if (Array.isArray(payload.tags) === false && typeof payload.tags === "string") {
        payload.tags = payload.tags.split(",").map((s) => s.trim()).filter(Boolean);
      }
      // Coerce highlights (lines) string → array
      if (Array.isArray(payload.highlights) === false && typeof payload.highlights === "string") {
        payload.highlights = payload.highlights.split("\n").map((s) => s.trim()).filter(Boolean);
      }
      if (publish === true) payload.status = "published";
      if (publish === false) payload.status = "draft";

      let saved;
      if (isEdit) saved = await api.adminUpdate(kind, id, payload);
      else saved = await api.adminCreate(kind, payload);
      toast.success(publish === true ? "Published — now live" : publish === false ? "Reverted to draft" : "Saved");
      if (!isEdit) navigate(`/admin/${kind}/edit/${saved.id}`, { replace: true });
      else setDoc(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Delete this item permanently? This cannot be undone.")) return;
    try { await api.adminDelete(kind, id); toast.success("Deleted"); navigate(`/admin/${kind}`); }
    catch { toast.error("Delete failed"); }
  };

  const grouped = useMemo(() => {
    const main = fields.filter((f) => !f.section);
    const seo = fields.filter((f) => f.section === "SEO");
    return { main, seo };
  }, [fields]);

  const isPublished = doc.status === "published";

  if (!meta) return <div>Unknown content type.</div>;
  if (loading) return <div className="text-ink-soft">Loading…</div>;

  return (
    <div data-testid={`admin-editor-${kind}`}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <Link to={`/admin/${kind}`} data-testid="admin-editor-back" className="inline-flex items-center gap-2 text-ink-soft hover:text-ink text-[11px] font-mono-editorial uppercase tracking-[0.22em]">
          <ArrowLeft size={14} /> Back to {kind}
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-mono-editorial uppercase tracking-[0.2em] px-2 py-1 rounded-full border ${isPublished ? "bg-ink text-paper border-ink" : "text-ink-soft border-ink-line"}`}>
            {doc.status || "draft"}
          </span>
          {isEdit && (
            <button onClick={remove} data-testid="admin-editor-delete" className="inline-flex items-center gap-1 px-3 py-2 text-xs text-ink-soft hover:text-red-600 border border-ink-line rounded-full">
              <Trash2 size={13} /> Delete
            </button>
          )}
          <button onClick={() => save(null)} disabled={saving} data-testid="admin-editor-save" className="inline-flex items-center gap-1 px-4 py-2 text-xs text-ink-soft hover:text-ink border border-ink-line rounded-full disabled:opacity-40">
            <Save size={13} /> Save Draft
          </button>
          <button
            onClick={() => save(!isPublished)}
            disabled={saving}
            data-testid="admin-editor-publish"
            className={`inline-flex items-center gap-1 px-4 py-2 text-xs font-medium rounded-full transition-colors disabled:opacity-40 ${
              isPublished ? "border border-ink-line text-ink-soft hover:text-ink" : "bg-ink text-paper hover:bg-gold-dark"
            }`}
          >
            {isPublished ? (<><EyeOff size={13} /> Unpublish</>) : (<><Eye size={13} /> Publish</>)}
          </button>
        </div>
      </div>

      <h1 className="mt-6 font-display text-ink text-3xl md:text-4xl leading-[1.06]">
        {isEdit ? `Edit ${meta.label.toLowerCase()}` : `New ${meta.label.toLowerCase()}`}
      </h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 editorial-card p-6 md:p-8 space-y-6">
          {grouped.main.map((f) => (
            <FieldRow key={f.key} field={f} value={doc[f.key]} onChange={(v) => setField(f.key, v)} />
          ))}
        </div>

        <div className="space-y-6">
          {grouped.seo.length > 0 && (
            <div className="editorial-card p-6">
              <div className="overline overline-muted mb-4">SEO</div>
              <div className="space-y-4">
                {grouped.seo.map((f) => (
                  <FieldRow key={f.key} field={f} value={doc[f.key]} onChange={(v) => setField(f.key, v)} />
                ))}
              </div>
            </div>
          )}

          {isEdit && doc.slug && doc.status === "published" && kind !== "magazines" && (
            <div className="editorial-card p-6">
              <div className="overline overline-muted mb-3">Preview</div>
              <a
                href={`${meta.publicPath}/${doc.slug}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-ink hover:text-gold-dark"
                data-testid="admin-editor-preview"
              >
                Open live preview →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FieldRow = ({ field, value, onChange }) => {
  const common = "w-full px-3 py-2.5 bg-paper border border-ink-line rounded text-ink text-sm outline-none focus:border-ink";
  return (
    <div>
      <label className="overline overline-muted block mb-2" htmlFor={`f-${field.key}`}>
        {field.label}{field.required && <span className="text-gold-dark ml-0.5">*</span>}
      </label>
      {field.type === "textarea" && (
        <textarea id={`f-${field.key}`} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={field.rows || 3} className={`${common} resize-y`} data-testid={`f-${field.key}`} />
      )}
      {field.type === "text" && (
        <input id={`f-${field.key}`} type="text" value={value || ""} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} className={common} data-testid={`f-${field.key}`} />
      )}
      {field.type === "date" && (
        <input id={`f-${field.key}`} type="date" value={(value || "").slice(0, 10)} onChange={(e) => onChange(e.target.value)} className={common} data-testid={`f-${field.key}`} />
      )}
      {field.type === "tags" && (
        <input id={`f-${field.key}`} type="text" value={Array.isArray(value) ? value.join(", ") : value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={`f-${field.key}`} placeholder="e.g. AI, Enterprise" />
      )}
      {field.type === "lines" && (
        <textarea id={`f-${field.key}`} value={Array.isArray(value) ? value.join("\n") : value || ""} onChange={(e) => onChange(e.target.value)} rows={4} className={`${common} resize-y`} data-testid={`f-${field.key}`} placeholder="One per line" />
      )}
      {field.type === "media" && (
        <MediaUpload value={value} onChange={onChange} accept="image/*" label="Upload image" testId={`f-${field.key}`} />
      )}
      {field.type === "media-pdf" && (
        <MediaUpload value={value} onChange={onChange} accept="application/pdf" label="Upload PDF" testId={`f-${field.key}`} />
      )}
      {field.type === "rich" && (
        <RichTextEditor value={value} onChange={onChange} testId={`f-${field.key}`} />
      )}
      {field.hint && <div className="text-[11px] text-ink-muted mt-1">{field.hint}</div>}
    </div>
  );
};

export default AdminEditorPage;
